import { useCallback, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import StopIcon from '@mui/icons-material/Stop'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useRecorder } from '../context/RecorderProvider'
import { formatDuration } from '../utils/trailGeo'
import { formatDistance } from '@utils/units'
import { useMeasurementUnit } from '@hooks/useMeasurementUnit'

/**
 * Persistent recording control that follows the user around the app while a
 * trail is being recorded. Rendered above the router by ProtectedMainLayout so
 * it stays put across navigation; only shown when a recording is in progress
 * (status !== 'idle' / 'error').
 *
 * Desktop: a draggable pill anchored bottom-right. Mobile: a fixed full-width
 * bottom bar. Tapping the body returns to the recorder page. It sits below the
 * app bar (which uses drawer + 1) so it never covers the header.
 */
export function FloatingRecorderBar() {
  const recorder = useRecorder()
  const unit = useMeasurementUnit()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))

  // Drag offset (desktop only), relative to the default bottom-right anchor.
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(
    null
  )
  const movedRef = useRef(false)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isDesktop) return
      dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: offset.x, baseY: offset.y }
      movedRef.current = false
      ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    },
    [isDesktop, offset.x, offset.y]
  )

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true
    // Drag toward the top-left moves the bottom-right-anchored pill, so invert.
    setOffset({ x: drag.baseX - dx, y: drag.baseY - dy })
  }, [])

  const handlePointerUp = useCallback(() => {
    dragRef.current = null
  }, [])

  // Don't show on the recorder page itself (it has full controls) or when idle.
  const isIdle = recorder.status === 'idle' || recorder.status === 'error'
  const onRecorderPage = location.pathname === '/trail-recorder/record'
  if (isIdle || onRecorderPage) return null

  const isRecording = recorder.status === 'recording'

  const handleBodyClick = () => {
    // Suppress the navigate if this pointer sequence was a drag.
    if (movedRef.current) return
    navigate('/trail-recorder/record')
  }

  const content = (
    <>
      {isDesktop && (
        <Box
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.secondary', touchAction: 'none' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {/* Status + live stats — tapping returns to the recorder. */}
      <Box
        onClick={handleBodyClick}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, cursor: 'pointer', minWidth: 0, overflow: 'hidden' }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: isRecording ? 'error.main' : 'text.secondary',
            whiteSpace: 'nowrap',
            // Pulse the dot while recording so it reads as live.
            ...(isRecording && {
              animation: 'recorderBlink 1.4s ease-in-out infinite',
              '@keyframes recorderBlink': { '50%': { opacity: 0.35 } },
            }),
          }}
        >
          {isRecording ? '● REC' : '❚❚ Paused'}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistance(recorder.distanceMeters, unit)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDuration(recorder.elapsedMs)}
        </Typography>
      </Box>

      {/* Pause / resume + stop. Stop hands off to the recorder page so the user
          can name and save the trail with full context. */}
      <Stack direction="row" spacing={0.5}>
        {isRecording ? (
          <IconButton size="small" onClick={recorder.pause} aria-label="Pause recording">
            <PauseIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={recorder.resume} aria-label="Resume recording">
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          color="error"
          onClick={() => navigate('/trail-recorder/record')}
          aria-label="Stop recording"
        >
          <StopIcon fontSize="small" />
        </IconButton>
      </Stack>
    </>
  )

  // Keep below the app bar (drawer + 1) so the header stays interactive.
  const zIndex = theme.zIndex.appBar - 1

  if (!isDesktop) {
    return (
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderRadius: 0,
        }}
      >
        {content}
      </Paper>
    )
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        right: 24 + offset.x,
        bottom: 24 + offset.y,
        zIndex,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 0.75,
        borderRadius: 6,
      }}
    >
      {content}
    </Paper>
  )
}
